import { ColDef } from "ag-grid-community";
import { RequisitionRow } from "../../recoil/types";
import { DeviceType } from "../../utils/hooks/useDeviceHook";
import ReportDataCell from "./ReportDataCell";
import RequestDataCell from "./RequestDataCell";
import { InactiveCell } from "./InactiveCell";
import { NameCell } from "./NameCell";
import { ReportColumnCell } from "./ReportColumnCell";
import { ReleasePatientCell } from "./PatientReleaseCell";
import { RequisitionActionCell } from "./RequisitionActionCell";
import {
  ReleasePatientData,
  AddOnModalData,
  SubTabName,
  TabName,
} from "./types";
import { NewReqTestsOrderedCell } from "../NewRequisition/NewReqTestsOrderedCell";
import { NewRequisitionType } from "../NewRequisition/types";
import {
  formatWithTimezoneOffset,
  formatPhoneNumberWithInternational,
  defaultSort,
  formatDateTimeFromEpoch,
} from "../../utils/helpers";
import {
  isOrderPhlebotomyRequested,
  NameAndActionsCell,
} from "../NewRequisition/NewReqNameAndActionsCell";
import { RhDHeaderCell } from "./RhDHeaderCell";
import { NewReqActionDraftCell } from "../NewRequisition/NewReqActionDraftCell";
import { NewReqOrderPlacedCell } from "../NewRequisition/NewReqOrderPlacedCell";

const getProviderSortRow = (data: unknown): RequisitionRow => {
  const cfcBannerRow = data as { kind?: string; row?: RequisitionRow };
  return cfcBannerRow?.kind === "cfcBanner" && cfcBannerRow.row
    ? cfcBannerRow.row
    : (data as RequisitionRow);
};

export const getColDefs = (
  tabType: TabName, // Grid column definitions
  deviceType: DeviceType,
  subTabType: SubTabName,
  setNewReqOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setPatientReleaseModal: React.Dispatch<
    React.SetStateAction<ReleasePatientData | undefined>
  >,
  setAddOnModal: React.Dispatch<
    React.SetStateAction<AddOnModalData | undefined>
  >,
  setDraftModal: React.Dispatch<
    React.SetStateAction<"saved" | "deleted" | "delete" | "unsaved" | undefined>
  >,
  releaseToPatient: boolean,
  salesRepUser: boolean,
  setShowPhlebotomySnackbar: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      patientName: string;
    }>
  >,
  onArchive?: (sfid: string) => void,
  onUnarchive?: (sfid: string) => void,
  onPrintError?: (message: string) => void,
  smallAddOnButton?: boolean,
  onEditOrder?: (order: NewRequisitionType) => void,
  editOrderEnabled?: boolean,
  onRequestReportUpdate?: (row: RequisitionRow) => void,
): ColDef[] => {
  if (tabType === "ORDERED") {
    if (subTabType === "DRAFTS") {
      return [
        {
          field: "name",
          minWidth: 300,
          cellRenderer: NameAndActionsCell,
          cellRendererParams: {
            actionBarEnabled: false,
            setShowPhlebotomySnackbar,
          },
          pinned: deviceType !== "mobile",
          sortable: true,
          valueGetter: (row) => `${row.data.first_name} ${row.data.last_name}`,
        },
        {
          field: "provider",
          minWidth: 200,
          maxWidth: 350,
          cellRenderer: InactiveCell,
          sortable: true,
          valueGetter: (row) => row.data.ordering_health_care_providers,
        },
        {
          field: "testType",
          cellRenderer: NewReqTestsOrderedCell,
          minWidth: 300,
          maxWidth: 300,
        },
        {
          field: "draftSaved",
          minWidth: 200,
          maxWidth: 350,
          cellRenderer: InactiveCell,
          sortable: true,
          initialSort: "desc",
          valueGetter: (row) => formatDateTimeFromEpoch(row.data.created_at),
          comparator: (valueA, valueB) =>
            defaultSort(new Date(valueA).getTime(), new Date(valueB).getTime()),
        },
        {
          field: "",
          minWidth: 200,
          cellRenderer: NewReqActionDraftCell,
          cellRendererParams: { setNewReqOpen, setDraftModal },
          valueGetter: (row) => row.data.unity_complete_id,
        },
      ];
    }

    return [
      {
        field: "name",
        minWidth: 250,
        flex: 2,
        cellRenderer: NameAndActionsCell,
        cellRendererParams: {
          actionBarEnabled: true,
          setShowPhlebotomySnackbar,
          onEditOrder,
          editOrderEnabled,
        },
        pinned: deviceType !== "mobile",
        sortable: true,
        valueGetter: (row) => `${row.data.first_name} ${row.data.last_name}`,
      },
      {
        field: "dob",
        minWidth: 150,
        maxWidth: 150,
        cellRenderer: InactiveCell,
        sortable: true,
        valueGetter: (row) => formatWithTimezoneOffset(row.data.date_of_birth),
        comparator: (valueA, valueB) =>
          defaultSort(new Date(valueA).getTime(), new Date(valueB).getTime()),
      },

      {
        field: "patientPhone",
        minWidth: 150,
        maxWidth: 350,
        cellRenderer: InactiveCell,
        sortable: true,
        valueGetter: (row) =>
          formatPhoneNumberWithInternational(row.data.cell_phone),
      },
      {
        field: "provider",
        minWidth: 200,
        maxWidth: 350,
        cellRenderer: InactiveCell,
        sortable: true,
        valueGetter: (row) => row.data.ordering_health_care_providers,
      },
      {
        field: "accountName",
        minWidth: 225,
        maxWidth: 350,
        cellRenderer: InactiveCell,
        sortable: true,
        valueGetter: (row) =>
          `${salesRepUser ? `${row.data.lims_id} / ` : ""}${
            row.data.clinic_name
          }`,
      },
      {
        field: "testType",
        cellRenderer: NewReqTestsOrderedCell,
        minWidth: 300,
        maxWidth: 300,
      },
      {
        field: "orderPlaced",
        minWidth: 200,
        maxWidth: 350,
        cellRenderer: NewReqOrderPlacedCell,
        sortable: true,
        initialSort: "desc",
        valueGetter: (row) => formatDateTimeFromEpoch(row.data.created_at),
        comparator: (valueA, valueB) =>
          defaultSort(new Date(valueA).getTime(), new Date(valueB).getTime()),
      },
      {
        field: "phlebotomy",
        minWidth: 150,
        maxWidth: 350,
        cellRenderer: InactiveCell,
        sortable: true,
        valueGetter: (row) =>
          isOrderPhlebotomyRequested(row.data) ? "Requested" : "Not Requested",
        hide: salesRepUser,
      },
    ];
  }
  const useActionCell = tabType === "COMPLETED" || tabType === "ARCHIVED";
  return [
    {
      field: "name",
      minWidth: 250,
      maxWidth: 500,
      valueGetter: (data) => {
        const row = getProviderSortRow(data.data);
        return {
          name: row.name,
          missingInfo: row.missingInfo,
        };
      },
      cellRenderer: useActionCell ? RequisitionActionCell : NameCell,
      cellRendererParams: {
        tabType,
        deviceType,
        onArchive,
        onUnarchive,
        onPrintError,
        onRequestReportUpdate: salesRepUser ? undefined : onRequestReportUpdate,
      },
      pinned: deviceType !== "mobile",
      sortable: true,
      comparator: (valueA, valueB) => {
        if (valueA.name.toLowerCase() === valueB.name.toLowerCase()) return 0;
        return valueA.name.toLowerCase() > valueB.name.toLowerCase() ? 1 : -1;
      },
    },
    {
      field: "lastArchivedOn",
      headerName: "Last Archived On",
      minWidth: 200,
      maxWidth: 250,
      cellRenderer: InactiveCell,
      valueGetter: (row) =>
        formatDateTimeFromEpoch(getProviderSortRow(row.data).archivedAt),
      sortable: true,
      initialSort: tabType === "ARCHIVED" ? "desc" : undefined,
      comparator: (valueA, valueB) =>
        defaultSort(new Date(valueA).getTime(), new Date(valueB).getTime()),
      hide: tabType !== "ARCHIVED",
    },
    {
      field: "lastArchivedBy",
      headerName: "Last Archived By",
      minWidth: 200,
      maxWidth: 300,
      cellRenderer: InactiveCell,
      valueGetter: (row) => getProviderSortRow(row.data).archivedByName || "",
      sortable: true,
      hide: tabType !== "ARCHIVED",
    },
    {
      field: "provider",
      minWidth: 200,
      cellRenderer: InactiveCell,
      valueGetter: (row) => getProviderSortRow(row.data).physician,
      cellClassRules: {
        "cell-missing-info": ({ data }) =>
          data && (data as RequisitionRow).missingInfo,
      },
      maxWidth: 300,
      sortable: true,
    },
    {
      field:
        tabType === "COMPLETED" || tabType === "ARCHIVED"
          ? "reported"
          : "collected",
      minWidth: 150,
      maxWidth: 150,
      cellRenderer: InactiveCell,
      valueGetter: (row) =>
        tabType === "COMPLETED" || tabType === "ARCHIVED"
          ? getProviderSortRow(row.data).reported
          : getProviderSortRow(row.data).collected,
      cellClassRules: {
        "cell-missing-info": ({ data }) =>
          data && (data as RequisitionRow).missingInfo,
      },
      sortable: true,
      initialSort: "desc",
      comparator: (valueA, valueB) =>
        defaultSort(new Date(valueA).getTime(), new Date(valueB).getTime()),
    },
    {
      field: "carrier",
      valueGetter: (params) => ({
        value: (params.data as RequisitionRow).carrier,
        carrierRead: (params.data as RequisitionRow).carrierRead,
        carrierMissing: (params.data as RequisitionRow).missingInfo,
      }),
      valueFormatter: (params) =>
        params.data && (params.data as RequisitionRow).carrier,
      cellRenderer: ReportDataCell,
      cellRendererParams: {
        tabType,
        deviceType,
        onPrintError,
      },
      cellClass: "grid-result-cell",
      minWidth: 200,
      maxWidth: 350,
    },
    {
      field: "aneuploidy",
      valueGetter: (params) => ({
        value: (params.data as RequisitionRow).aneuploidy,
        aneuploidyRead: (params.data as RequisitionRow).aneuploidyRead,
        aneuploidyMissing: (params.data as RequisitionRow).missingInfo,
      }),
      valueFormatter: (params) =>
        params.data && (params.data as RequisitionRow).aneuploidy,
      cellRenderer: ReportDataCell,
      cellRendererParams: {
        tabType,
        deviceType,
        onPrintError,
      },
      cellClass: "grid-result-cell",
      minWidth: 200,
      maxWidth: 350,
    },
    {
      field: "rhd",
      cellRenderer: RequestDataCell,
      headerComponent: RhDHeaderCell,
      minWidth: smallAddOnButton ? 180 : 260,
      headerClass: "rhd-header",
      headerName: "RhD/Fetal Antigen",
      valueGetter: (params) => {
        const row = params.data as RequisitionRow;
        return `${row.RhD}|${row.rhdReleaseStatus}|${row.faReleaseStatus}`;
      },
      cellRendererParams: { setAddOnModal, tabType, deviceType },
      cellClass: "grid-result-cell",
      maxWidth: 400,
    },
    {
      field: "22q11.2",
      cellRenderer: ReportDataCell,
      minWidth: 150,
      headerClass: "rhd-header",
      headerName: "22q11.2",
      valueGetter: (params) => (params.data as RequisitionRow).del22q,
      cellClass: "grid-result-cell",
      maxWidth: 250,
    },
    {
      field: "fetalSex",
      cellRenderer: RequestDataCell,
      cellRendererParams: { setAddOnModal, tabType, deviceType },
      cellClass: "grid-result-cell",
      minWidth: smallAddOnButton ? 180 : 260,
    },
    {
      field: "patientStatus",
      cellRenderer: ReleasePatientCell,
      valueGetter: (params) => ({
        carrierCellStatus: (params.data as RequisitionRow).carrierReleaseStatus,
        aneuploidyReleaseStatus: (params.data as RequisitionRow)
          .aneuploidyReleaseStatus,
      }),
      cellRendererParams: { setPatientReleaseModal, tabType, deviceType },
      hide:
        tabType === "PROCESSING" || tabType === "ARCHIVED" || !releaseToPatient,
      minWidth: 225,
      maxWidth: 225,
    },
    {
      field: "Reports",
      cellRenderer: ReportColumnCell,
      minWidth: 150,
      maxWidth: 150,
      hide: tabType === "ARCHIVED",
    },
  ];
};
